import { yupResolver } from '@hookform/resolvers/yup'
import { LineFileText } from '@qr-front/icons/line-file-text'
import { Checkbox } from '@qr-front/ui/components/Checkbox'
import { useAtom, useAtomValue, useStore } from 'jotai'
import { atomWithReset } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { useBus } from 'react-bus'
import { FormProvider, useForm } from 'react-hook-form'
import { TUploadedMedia } from 'services/mediaStorage'

import { TTranslationFunction } from '@/types'

import {
	CLOSE_CONTENT_ACCORDIONS_EVENT,
	OPEN_CONTENT_ACCORDIONS_EVENT,
} from '../../contentFormConstants'
import { CONTENT_DESIGN_FORM_ID } from '../client'
import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { useSyncFormSectionValidity } from '../useSyncFormSectionValidity'
import { DOCUMENT_INFO_FORM_ID } from './DocumentInfo'
import { getPdfFormSchema } from './pdfFormModel'
import { PdfUpload } from './PdfUpload'

export const PDF_FILE_FORM_ID = 'pdfFile'

export const pdfFileAtom = atomWithReset<File | null>(null)
export const pdfAtom = atomWithReset<TUploadedMedia | null>(null)
export const pdfIsDirectAtom = atomWithReset<boolean>(false)

export const PdfFileInfo = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()

	const bus = useBus()

	const pdfUploadRef = useRef<TContentFormHandle>(null)
	const [pdfIsDirect, setPdfIsDirect] = useAtom(pdfIsDirectAtom)

	const pdfFile = useAtomValue(pdfAtom)

	const store = useStore()

	const form = useForm({
		defaultValues: {
			isDirect: store.get(pdfIsDirectAtom),
			file: store.get(pdfAtom) || undefined,
		},
		mode: 'onTouched',
		resolver: yupResolver(getPdfFormSchema(t)),
		reValidateMode: 'onChange',
		shouldUnregister: false,
	})

	useSyncFormSectionValidity({
		formId: PDF_FILE_FORM_ID,
		isValid: !!pdfFile,
	})

	useImperativeHandle(
		ref,
		() => ({
			validate: () => {
				return pdfUploadRef!.current!.validate()
			},
		}),
		[],
	)

	useEffect(() => {
		if (pdfIsDirect) {
			setTimeout(() => {
				bus.emit(CLOSE_CONTENT_ACCORDIONS_EVENT, [
					CONTENT_DESIGN_FORM_ID,
					DOCUMENT_INFO_FORM_ID,
				])
			}, 0)
		}
	}, [bus, pdfIsDirect])

	const handlePdfIsDirectChange = (checked: boolean) => {
		setPdfIsDirect(!!checked)

		if (checked) {
			bus.emit(CLOSE_CONTENT_ACCORDIONS_EVENT, [
				CONTENT_DESIGN_FORM_ID,
				DOCUMENT_INFO_FORM_ID,
			])
		} else {
			bus.emit(OPEN_CONTENT_ACCORDIONS_EVENT, [
				CONTENT_DESIGN_FORM_ID,
				DOCUMENT_INFO_FORM_ID,
			])
		}
	}

	return (
		<FormProvider {...form}>
			<div className="flex flex-col gap-6">
				<PdfUpload ref={pdfUploadRef} />
				<div className="flex w-full flex-col gap-4 tablet:flex-row tablet:gap-12">
					<Checkbox
						checked={pdfIsDirect}
						onCheckedChange={handlePdfIsDirectChange}
						name="isDirect"
						id="show-pdf"
						label={t('generator__content_form_section__pdf__full_screen')}
					/>
				</div>
			</div>
		</FormProvider>
	)
})

PdfFileInfo.displayName = 'PdfFileInfo'

export const pdfFileFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: PDF_FILE_FORM_ID,
	title: t('generator__content_form_section__pdf__file_title'),
	description: t('generator__content_form_section__pdf__file_description'),
	icon: <LineFileText className="text-info-color fill-info-color" />,
	Content: PdfFileInfo,
})
